import clsx from 'clsx';
import React, { FC } from 'react';

import { formatCurrency, formatDecimal, FormattedMessage, useIntl } from '@futrli/localisation';
import { PerformanceMetricsCashflowUnpaidInvoiceBill } from '@futrli-sdk/predict';

import parentStyles from '../performance.scss';
import styles from './top-owed.scss';

interface Props {
  type: 'invoices' | 'bills';
  currency: string;
  data: PerformanceMetricsCashflowUnpaidInvoiceBill[];
}

export const TopOwed: FC<Props> = ({ type, currency, data }: Props) => {
  const intl = useIntl();
  const headers = [
    type === 'invoices' ? (
      <FormattedMessage key="0" id="predict.invoice_date" defaultMessage="invoice date" />
    ) : (
      <FormattedMessage key="0" id="predict.bill_date" defaultMessage="bill date" />
    ),
    <FormattedMessage key="1" id="predict.due_date" defaultMessage="Due date" />,
    type === 'invoices' ? (
      <FormattedMessage key="2" id="predict.hitting_cash_on" defaultMessage="hitting cash on" />
    ) : (
      <FormattedMessage key="2" id="predict.leaving_cash_on" defaultMessage="leaving cash on" />
    ),
    <FormattedMessage key="3" id="predict.percent_owed" defaultMessage="% of owed" />
  ];
  return (
    <section data-testid={`top-${type}-owed`} className={clsx(parentStyles.card)}>
      <h3 className={clsx(parentStyles.cardTitle)}>
        {type === 'invoices' ? (
          <FormattedMessage id="predict.top-invoices-due" defaultMessage="Top invoices due" />
        ) : (
          <FormattedMessage id="predict.top-bills-owed" defaultMessage="Top bills owed" />
        )}
      </h3>
      <h4 className={clsx(parentStyles.cardDesc)} style={{ marginBottom: 10 }}>
        <FormattedMessage id="predict.last_month_and_prior_11" defaultMessage="Last month and the prior 11" />
      </h4>
      {data.length ? (
        <table className={clsx(styles.table)}>
          <thead>
            <tr>
              <td colSpan={2}></td>
              {headers.map(h => (
                <th key={h.key} className={clsx('uiTypeLabel')}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} data-testid="data-row">
                <td className={clsx('uiTypeFont16Regular')}>{formatCurrency(+row.amount_due, intl, currency, 0, 0)}</td>
                <td className={clsx(styles.name, 'uiTypeFont12Regular')}>{row.contact_name}</td>
                <td className={clsx('uiTypeFont16Regular')}>
                  {intl.formatDate(new Date(row.raised_date), {
                    day: 'numeric',
                    month: 'short',
                    year: '2-digit'
                  })}
                </td>
                <td className={clsx('uiTypeFont16Regular')}>
                  {intl.formatDate(new Date(row.due_date), {
                    day: 'numeric',
                    month: 'short',
                    year: '2-digit'
                  })}
                </td>
                <td className={clsx('uiTypeFont16Regular')}>
                  {intl.formatDate(new Date(row.expected_paid_date), {
                    day: 'numeric',
                    month: 'short',
                    year: '2-digit'
                  })}
                </td>
                <td className={clsx('uiTypeFont16Regular')}>
                  {formatDecimal(row.percent_of_owed * 100, intl, 0, 0)}
                  {'%'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className={clsx('uiTypeFont14RegularLight', styles.noData)} data-testid="no-data-text">
          {type === 'invoices' ? (
            <FormattedMessage id="predict.no_unpaid_invoices" defaultMessage="We aren't seeing any unpaid invoices." />
          ) : (
            <FormattedMessage id="predict.no_unpaid_bills" defaultMessage="We aren't seeing any unpaid bills." />
          )}
        </p>
      )}
    </section>
  );
};
